/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */
import {
    handle_picklist_action,
    makeSetDataArrayAction,
    PICKLIST_INITIAL_STATE,
    makeDeselectSelectedItemAction,
    DATA_DOCUMENT_ID_INDEX,
    DATA_ARRAY_INDEX_INDEX,
    DATA_LONG_NAME_INDEX
} from "mdx-ivcomp-common-ui/dist/src/components/ivpicklist/iv-picklist-redux/iv-picklist-redux";
import {fetchAndHandleResponse} from "../../../../utils";
import {immutable_set} from "../../../../../../utils";
import {getEnteralFormulasListPromise} from "../../../../../../services/neofaxPediatricsService";
import {
    PER_100_CAL,
    FORMULA_SEARCH_PANEL_NAME,
    NEOFAX_FORMULA_PICKLIST_ID,
    PEDIATRICS_FORMULA_PICKLIST_ID, RESULT_TYPE_PEDIATRICS, RESULT_TYPE_NEOFAX
} from "../../../../common/constants";

const A = {
    CURRENT_PICKLIST_ID: 'CURRENT_PICKLIST_ID',
    FORMULA_DISPLAY_CHOICE: 'FORMULA_DISPLAY_CHOICE',
    SET_FORMULA_SEARCH_ERROR_MESSAGE: 'SET_FORMULA_SEARCH_ERROR_MESSAGE',
    SET_FORMULA_SEARCH_STATUS: 'SET_FORMULA_SEARCH_STATUS',
    CLEAR_FORMULA_SEARCH_STATE: 'CLEAR_FORMULA_SEARCH_STATE',
}

const ENHANCED_PICKLIST_INITIAL_STATE = {
    ...PICKLIST_INITIAL_STATE,
    _formula_loading_status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    _formula_loading_error: ""
}

export const initialState = {
    current_picklist_id: NEOFAX_FORMULA_PICKLIST_ID,
    display_choice: PER_100_CAL, // per100Cal | perLiter | both
    [NEOFAX_FORMULA_PICKLIST_ID]: ENHANCED_PICKLIST_INITIAL_STATE,
    [PEDIATRICS_FORMULA_PICKLIST_ID]: ENHANCED_PICKLIST_INITIAL_STATE,
}

export const loadingState = new Map([
    [NEOFAX_FORMULA_PICKLIST_ID, false],
    [PEDIATRICS_FORMULA_PICKLIST_ID, false]
]);

// selectors ...
export const selectFormulaDisplayChoice = (global_state) => {
    return global_state[FORMULA_SEARCH_PANEL_NAME].display_choice;
}

export const selectDisableButtons = (global_state) => {
    const formula_search_state = global_state[FORMULA_SEARCH_PANEL_NAME];
    const picklistId = formula_search_state.current_picklist_id;
    const picklistState = formula_search_state[picklistId];
    const selected_items = picklistState?.selected_items || [];
    return selected_items.length === 0;
}

export const selectCurrentPicklistId = (global_state) => {
    return global_state[FORMULA_SEARCH_PANEL_NAME].current_picklist_id;
}

export const selectViewData = (global_state) => {
    const formula_search_state = global_state[FORMULA_SEARCH_PANEL_NAME];
    const display_choice = formula_search_state.display_choice;
    const picklistId = formula_search_state.current_picklist_id;
    const picklistState = formula_search_state[picklistId];
    const selected_items = picklistState?.selected_items || [];
    const selected_formula_ids = selected_items.map((item) => {
        return item[DATA_DOCUMENT_ID_INDEX]; // formula ID
    }).join(",");
    const selected_formula_names = selected_items.map((item) => item[DATA_LONG_NAME_INDEX]);
    return {
        display_choice,
        picklistId,
        selected_formula_ids,
        selected_formula_names
    }
}

// actions ...
export const makeSetCurrentPicklistIdAction = (picklist_id) => {
    return {
        type: A.CURRENT_PICKLIST_ID,
        picklist_id: picklist_id,
    }
}

export const makeSetDisplayChoiceAction = (new_display_choice) => {
    return {
        type: A.FORMULA_DISPLAY_CHOICE,
        display_choice: new_display_choice,
    }
}

export const makeSetStatusAction = (picklistId, new_status, error_msg="") => {
    return {
        type: A.SET_FORMULA_SEARCH_STATUS,
        payload: {
            picklistId,
            new_status,
            error_msg
        }
    }
}

export const makeErrorDataAction = (picklistId, error_msg) => {
    return {
        type: A.SET_FORMULA_SEARCH_ERROR_MESSAGE,
        payload: {
            picklistId,
            error_msg
        }
    }
}

export function makeClearFormulaSearchState() {
    return {
        type: A.CLEAR_FORMULA_SEARCH_STATE,
    }
}

// thunks ...
export const processResponse = (responseBody, dispatch, params) => {
    const {picklist_id, page_name} = params;
    if (responseBody && responseBody.recordSet?.records) {
        const data_array = prepareFormulaDataArray(responseBody.recordSet?.records);
        dispatch(
            makeSetDataArrayAction(
                data_array,
                picklist_id,
                page_name,
                true
            )
        );
    }
};

export const prepareFormulaDataArray = (payload) => {
    return payload.map(item => [item.title, item.docId]);
};

export const makeFormulaLoadActionThunk = (picklist_id, history, login_page, loadingState) => async (dispatch, getState) => {
    if (loadingState.get(picklist_id)) {
        // If data is already loaded, no need to fetch again
        return;
    }

    loadingState.set(picklist_id, true);
    const fetcher = async () => {
        const result_type = picklist_id === NEOFAX_FORMULA_PICKLIST_ID ? RESULT_TYPE_NEOFAX : RESULT_TYPE_PEDIATRICS;
        return await getEnteralFormulasListPromise({result_type, login_page});
    }
    const rejectWithValue = (error_data) => {
        dispatch(makeErrorDataAction(picklist_id, error_data));
    }
    const params = {
        // params used by: fetchAndHandleResponse
        fetcher,
        history,
        loginPage: login_page,

        // params used by: processResponse
        picklist_id,
        page_name: FORMULA_SEARCH_PANEL_NAME,
    };
    const responseBody = await fetchAndHandleResponse(
        params,
        dispatch,
        rejectWithValue,
        processResponse
    );

    loadingState.set(picklist_id, false);

    return responseBody;
}

export const makeDeleteSelectedFormulaThunk = (docId) => (dispatch, getState) => {
    const global_state = getState();
    const picklistId = global_state[FORMULA_SEARCH_PANEL_NAME].current_picklist_id;
    const picklist_state = global_state[FORMULA_SEARCH_PANEL_NAME][picklistId];
    const selected_formula = picklist_state.selected_items.find(formula => formula[DATA_DOCUMENT_ID_INDEX] === docId);
    if (selected_formula) {
      const data_array_index = selected_formula[DATA_ARRAY_INDEX_INDEX];
      dispatch(makeDeselectSelectedItemAction(
        data_array_index,
        picklistId,
        FORMULA_SEARCH_PANEL_NAME
      ));
    }

}

// reducers ...
const setCurrentPicklistIdReducer = (local_state, action) => {
    const {picklist_id} = action;
    const new_state = immutable_set(local_state, "current_picklist_id", picklist_id);
    return new_state;
}

const setFormulaDisplayChoiceReducer = (local_state, action) => {
    const {display_choice} = action;
    const new_state = immutable_set(local_state, "display_choice", display_choice);
    return new_state;
}

const setStatusReducer = (local_state, action) => {
    const {picklistId, new_status, error_msg} = action.payload;
    let new_state = immutable_set(local_state,[picklistId, "_formula_loading_status"], new_status);
    new_state = immutable_set(new_state, [picklistId, "_formula_loading_error"], error_msg);
    return new_state;
}

const setErrorMessageReducer = (local_state, action) => {
    const {picklistId, error_msg} = action.payload;
    const new_state = immutable_set(local_state,[picklistId, "_formula_loading_error"], error_msg);
    return new_state;
}

const clearFormulaSearchStateReducer = (local_state, action) => {
    return initialState;
}

export const formulaSearchPageReducer = (current_state = initialState, action) => {
    switch (action.type) {
        case A.CURRENT_PICKLIST_ID:
            return setCurrentPicklistIdReducer(current_state, action);
        case A.FORMULA_DISPLAY_CHOICE:
            return setFormulaDisplayChoiceReducer(current_state, action);
        case A.SET_FORMULA_SEARCH_STATUS:
            return setStatusReducer(current_state, action);
        case A.SET_FORMULA_SEARCH_ERROR_MESSAGE:
            return setErrorMessageReducer(current_state, action);
        case A.CLEAR_FORMULA_SEARCH_STATE:
            return clearFormulaSearchStateReducer(current_state, action);
        default:
            const picklist_id = action.picklist_id;
            if (!picklist_id) {
                return current_state;
            } else {
                const new_state = handle_picklist_action(
                    FORMULA_SEARCH_PANEL_NAME,
                    current_state,
                    action
                );
                if (new_state !== null) {
                    return new_state;
                } else {
                    return current_state;
                }
            }
    }
}

export default formulaSearchPageReducer;